/**
 * useProductYachtRouteData Hook
 * 
 * Yacht Route product verilerini parse eden ve transform eden custom hook
 */

import { useMemo } from 'react';

export interface RouteDay {
    id: string;
    dayNumber: number;
    title: string;
    description: string | null;
    images: string[];
    imageIds: number[];
    location_type: string | null;
    location_title: string | null;
    location_address: string | null;
    lat: number | null;
    lng: number | null;
    arrival_time: string | null;
    departure_time: string | null;
    duration_hours: number | null;
    activities: string | null;
}

export interface RouteImage {
    imageUrl: string;
    thumbImageUrl?: string;
    imageAlt: string;
    title?: string;
    description?: string;
    link?: string;
}

export interface UseProductYachtRouteDataReturn {
    routeName: string | null;
    routeHighlights: string | null;
    durationHours: number | null;
    durationDays: number | null;
    startLocation: string | null;
    endLocation: string | null;
    routeType: string | null;
    routeProgram: RouteDay[];
    routeImages: RouteImage[];
    tabFields: Array<{
        tabName: string;
        sectionId: string;
        fields: Array<{
            fieldKey: string;
            fieldTitle: string;
            content: string;
        }>;
    }>;
}

export function useProductYachtRouteData(
    product: any,
    language: string = 'en'
): UseProductYachtRouteDataReturn {
    const productId = product?.id;
    const productMetas = product?.metas;
    const productFields = product?.fields;

    return useMemo(() => {
        const metas = productMetas || {};
        const fields = productFields || {};

        // Route name
        const routeName = metas.route_name || fields.route_name?.field_value || null;

        // Route highlights
        const routeHighlights = metas.route_highlights || fields.route_highlights?.field_value || null;

        // Duration
        const durationHours = metas.duration_hours 
            ? parseFloat(metas.duration_hours) 
            : (fields.duration_hours?.field_value ? parseFloat(fields.duration_hours.field_value) : null);
        
        const durationDays = metas.duration_days 
            ? parseInt(metas.duration_days) 
            : (fields.duration_days?.field_value ? parseInt(fields.duration_days.field_value) : null);

        // Locations
        const startLocation = metas.start_location || fields.start_location?.field_value || null;
        const endLocation = metas.end_location || fields.end_location?.field_value || null;

        // Route type
        const routeType = metas.route_type || fields.route_type?.field_value || null;

        // Route program (repeater field)
        const routeProgram: RouteDay[] = [];
        const programData = metas.route_program || fields.route_program?.field_value;
        
        if (Array.isArray(programData)) {
            programData.forEach((day: any, index: number) => {
                if (!day) return;
                
                const dayNumber = day.day || day.dayNumber || index + 1;
                const title = day.title || '';
                const description = day.description || null;
                
                // Images - JSON array veya object array
                let images: string[] = [];
                const imageIds: number[] = [];
                if (day.images) {
                    if (typeof day.images === 'string') {
                        try {
                            const parsed = JSON.parse(day.images);
                            if (Array.isArray(parsed)) {
                                images = parsed.filter((img: any) => typeof img === 'string');
                            }
                        } catch {
                            // String ise direkt kullan
                            images = [day.images].filter(Boolean);
                        }
                    } else if (Array.isArray(day.images)) {
                        images = day.images
                            .map((img: any) => {
                                if (typeof img === 'string') return img;
                                if (img?.url) return img.url;
                                if (img?.path) return img.path;
                                return null;
                            })
                            .filter(Boolean);
                    }
                }

                routeProgram.push({
                    id: `day-${dayNumber}-${index}`,
                    dayNumber: typeof dayNumber === 'number' ? dayNumber : parseInt(dayNumber) || index + 1,
                    title: typeof title === 'string' ? title : String(title || `Day ${dayNumber}`),
                    description: description ? String(description) : null,
                    images,
                    imageIds,
                    location_type: day.location_type || null,
                    location_title: day.location_title || null,
                    location_address: day.location_address || null,
                    lat: day.lat ? parseFloat(day.lat) : null,
                    lng: day.lng ? parseFloat(day.lng) : null,
                    arrival_time: day.arrival_time || null,
                    departure_time: day.departure_time || null,
                    duration_hours: day.duration_hours ? parseFloat(day.duration_hours) : null,
                    activities: day.activities || null,
                });
            });
        }

        // Route images (repeater field for gallery)
        const routeImages: RouteImage[] = [];
        const imagesData = metas.route_images || fields.route_images?.field_value;
        
        if (Array.isArray(imagesData)) {
            imagesData.forEach((item: any) => {
                if (!item) return;
                
                const imageUrl = item.image_url || item.imageUrl || '';
                if (!imageUrl) return;

                routeImages.push({
                    imageUrl: String(imageUrl),
                    thumbImageUrl: item.thumb_image_url || item.thumbImageUrl || undefined,
                    imageAlt: item.image_alt || item.imageAlt || '',
                    title: item.title || undefined,
                    description: item.description || undefined,
                    link: item.link || undefined,
                });
            });
        }

        // Tab fields (rich_text field'ları tab_names ile gruplanmış)
        const tabFields: Array<{
            tabName: string;
            sectionId: string;
            fields: Array<{
                fieldKey: string;
                fieldTitle: string;
                content: string;
            }>;
        }> = [];

        // Tab group'larına göre field'ları grupla
        const tabGroups = new Map<string, Array<{ fieldKey: string; fieldTitle: string; content: string }>>();
        
        Object.entries(fields).forEach(([key, field]: [string, any]) => {
            if (!field || typeof field !== 'object') return;
            
            const tabGroupKey = field.tab_group_key;
            const tabSectionId = field.tab_section_id || tabGroupKey;
            const fieldValue = field.field_value;
            const fieldTitle = field.field_title || field.field_titles?.[language] || key;
            const tabName = field.tab_names?.[language] || field.tab_name || tabGroupKey || 'Information';

            if (tabGroupKey && fieldValue) {
                if (!tabGroups.has(tabGroupKey)) {
                    tabGroups.set(tabGroupKey, []);
                }
                
                tabGroups.get(tabGroupKey)!.push({
                    fieldKey: key,
                    fieldTitle: String(fieldTitle),
                    content: String(fieldValue),
                });
            }
        });

        // Tab fields'ı oluştur
        tabGroups.forEach((fields, tabGroupKey) => {
            const firstField = fields[0];
            const field = Object.values(fields).find(f => f.fieldKey === tabGroupKey) || firstField;
            const tabName = fields[0]?.fieldTitle || tabGroupKey;
            const sectionId = tabGroupKey.replace(/_/g, '-');

            tabFields.push({
                tabName: String(tabName),
                sectionId,
                fields,
            });
        });

        return {
            routeName,
            routeHighlights,
            durationHours,
            durationDays,
            startLocation,
            endLocation,
            routeType,
            routeProgram,
            routeImages,
            tabFields,
        };
    }, [productId, productMetas, productFields, language]);
}

